/**
 * Locale-aware date and time formatting helpers and display components.
 *
 * Provides relative timestamps (`fromNow`, `fromNowCompact`), locale-specific
 * date-range formatting (`formatToDateRangeString`), and display components
 * `<DateTimeDisplay>`, `<DateDisplay>`, and `<MonthAbbreviation>`. dayjs locale
 * modules are registered explicitly via `dayjs.locale(locale, undefined, true)`
 * rather than bare side-effect imports — Metro/Expo tree-shaking strips bare
 * side-effect imports from the production bundle, causing dayjs to silently
 * fall back to English month names. Don't simplify that pattern.
 */

import { fromNow as _sharedFromNow, getDayjsLocaleFromIso, getLocaleDateFormat, getLocaleTimeOfDayFormat } from '@repo/shared/date';
import dayjs from 'dayjs';
import localeDe from 'dayjs/locale/de';
import localeDeCh from 'dayjs/locale/de-ch';
import localeFr from 'dayjs/locale/fr';
import localeFrCa from 'dayjs/locale/fr-ca';
import localeFrCh from 'dayjs/locale/fr-ch';
import localeIt from 'dayjs/locale/it';
import localeItCh from 'dayjs/locale/it-ch';
import localeNl from 'dayjs/locale/nl';

// Explicitly register each locale with dayjs. Bare side-effect imports (import
// 'dayjs/locale/fr') get stripped from the production bundle by Metro/Expo
// tree-shaking, causing dayjs to silently fall back to English month names.
dayjs.locale(localeDe, undefined, true);
dayjs.locale(localeDeCh, undefined, true);
dayjs.locale(localeFr, undefined, true);
dayjs.locale(localeFrCa, undefined, true);
dayjs.locale(localeFrCh, undefined, true);
dayjs.locale(localeIt, undefined, true);
dayjs.locale(localeItCh, undefined, true);
dayjs.locale(localeNl, undefined, true);

import { type IsoLocale } from '@repo/shared/locale';
import { useTheme } from '@repo/theme';
import { useDatastore } from 'util/datastore';
import { isE2eTestMode } from 'util/e2e-test-mode';
import { t } from 'util/stdtypes';

import type { TextType } from './text';
import { UtilityText } from './text';
import { translateLabel, useLanguage } from './translation';

/**
 * @see See [Localization Docs](../../docs/contributing/localization.md) for
 * instructions.
 */

function _prepareDatesFromMs(timestamp: number | undefined) {
    if (timestamp === undefined || isNaN(timestamp)) {
        return null;
    }
    const currentDate = dayjs();
    const inputDate = dayjs(timestamp);
    return { currentDate, inputDate };
}

const E2E_DATE_PLACEHOLDER = '[date]';

/** Client wrapper around `@repo/shared/date`'s `fromNow` that returns a
 *  deterministic placeholder during e2e tests. */
export function fromNow(timestamp: number | string | undefined, language: IsoLocale = 'en-US'): string {
    if (isE2eTestMode()) return E2E_DATE_PLACEHOLDER;
    return _sharedFromNow(timestamp, language);
}

export function fromNowCompact(timestamp: number | undefined, language: IsoLocale = 'en-US') {
    if (isE2eTestMode()) return E2E_DATE_PLACEHOLDER;
    const dates = _prepareDatesFromMs(timestamp);
    if (!dates) {
        return '';
    }
    const { currentDate, inputDate } = dates;

    const diffInYears = currentDate.diff(inputDate, 'year');
    if (diffInYears > 0) {
        return translateLabel({ label: t('date.years_abbrev', '{years}y'), language, formatParams: { years: diffInYears } });
    }

    const diffInMonths = currentDate.diff(inputDate, 'month');
    if (diffInMonths > 0) {
        return translateLabel({ label: t('date.months_abbrev', '{months}mo'), language, formatParams: { months: diffInMonths } });
    }

    const diffInDays = currentDate.diff(inputDate, 'day');
    if (diffInDays > 0) {
        return translateLabel({ label: t('date.days_abbrev', '{days}d'), language, formatParams: { days: diffInDays } });
    }

    const diffInHours = currentDate.diff(inputDate, 'hour');
    if (diffInHours > 0) {
        return translateLabel({ label: t('date.hours_abbrev', '{hours}h'), language, formatParams: { hours: diffInHours } });
    }

    const diffInMinutes = currentDate.diff(inputDate, 'minute');
    if (diffInMinutes > 0) {
        return translateLabel({ label: t('date.minutes_abbrev', '{minutes}m'), language, formatParams: { minutes: diffInMinutes } });
    }

    return translateLabel({ label: t('date.now', 'now'), language });
}

export type DateTimeDisplayProps = {
    timestamp: number;
    color?: string;
    type?: TextType;
};

export function DateTimeDisplay({ timestamp, color, type = 'small' }: DateTimeDisplayProps) {
    const { colors } = useTheme();
    const resolvedColor = color ?? colors.colorTextPrimary;
    const datastore = useDatastore();
    if (isE2eTestMode()) {
        return <UtilityText color={resolvedColor} text={E2E_DATE_PLACEHOLDER} type={type} />;
    }
    const isoLocale = datastore.getLanguage() || 'en-US';
    const dayjsLocale = getDayjsLocaleFromIso(isoLocale);

    const dateTimeFormat = getLocaleDateFormat(isoLocale, { showYear: true }) + ' | ' + getLocaleTimeOfDayFormat(isoLocale);
    const text = dayjs(timestamp).locale(dayjsLocale).format(dateTimeFormat);

    return <UtilityText color={resolvedColor} text={text} type={type} />;
}

export function DateDisplay({ timestamp, color, type = 'small' }: DateTimeDisplayProps) {
    const { colors } = useTheme();
    const resolvedColor = color ?? colors.colorTextPrimary;
    const datastore = useDatastore();
    if (isE2eTestMode()) {
        return <UtilityText color={resolvedColor} text={E2E_DATE_PLACEHOLDER} type={type} />;
    }
    const isoLocale = datastore.getLanguage() || 'en-US';
    const dayjsLocale = getDayjsLocaleFromIso(isoLocale);

    const dateTimeFormat = getLocaleDateFormat(isoLocale, { showYear: true });
    const text = dayjs(timestamp).locale(dayjsLocale).format(dateTimeFormat);

    return <UtilityText color={resolvedColor} text={text} type={type} />;
}

function _formatDay({ day, locale }: { day: number; locale: string }) {
    if (locale === 'fr-CA' && day === 1) {
        return `${day}er`;
    }
    return day;
}

export function formatToDateRangeString({
    start = new Date(Date.UTC(2024, 5, 4, 4, 0, 0)),
    end = new Date(Date.UTC(2024, 5, 20, 4, 0, 0)),
    locale = 'fr-CA',
}): string {
    const options: Intl.DateTimeFormatOptions = {
        month: 'long',
    };
    const startDay = start.getDate();
    const endDay = end.getDate();
    const endYear = end.getFullYear();
    const isSameMonth = start.getMonth() === end.getMonth();

    switch (locale) {
        case 'de-DE':
            // Format to either '10. - 15. Mai 2024' if the range is in the same
            // month, or '10. Mai - 15. Juni 2024' if the range is over multiple
            // months
            return `${_formatDay({ day: startDay, locale })}.${!isSameMonth ? ` ${start.toLocaleDateString(locale, options)}` : ''} - ${_formatDay({ day: endDay, locale })}. ${end.toLocaleDateString(locale, options)} ${endYear}`;
        case 'fr-CA':
            // Format to either '10 - 15 mai' if the range is in the same month,
            // or '10 mai - 15 juin' if the range is over multiple months
            return `${_formatDay({ day: startDay, locale })}${!isSameMonth ? ` ${start.toLocaleDateString(locale, options)}` : ''} - ${_formatDay({ day: endDay, locale })} ${end.toLocaleDateString(locale, options)} ${endYear}`;
        default:
            // Format to either 'May 10 - 15' if the range is in the same month,
            // or 'May 10 - June 15' if the range is over multiple months
            return `${start.toLocaleDateString(locale, options)} ${_formatDay({ day: startDay, locale })} - ${!isSameMonth ? `${end.toLocaleDateString(locale, options)} ` : ''}${_formatDay({ day: endDay, locale })}, ${endYear}`;
    }
}

export type MonthAbbreviationProps = { date: Date; locale?: string };

export function MonthAbbreviation({ date, locale }: MonthAbbreviationProps) {
    const clientLocale = useLanguage();
    if (isE2eTestMode()) {
        return <UtilityText text={E2E_DATE_PLACEHOLDER} />;
    }
    const resolvedLocaleKey = locale || clientLocale;
    const resolvedDayjsLocale = getDayjsLocaleFromIso(resolvedLocaleKey);

    return <UtilityText text={dayjs(date).locale(resolvedDayjsLocale).format('MMM')} />;
}
